import axios from 'axios';

// Types for authentication
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}

// API response structure
export interface AuthResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      id: number;
      username: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  };
  message?: string;
}

// Use environment variable or default to localhost:8000
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with default config
const authClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const AuthService = {
  // Login user and get token
  async login(credentials: LoginCredentials): Promise<any> {
    try {
      const response = await authClient.post<AuthResponse>('/auth/login', credentials);
      
      // Check for success in the response
      if (response.data.success) {
        // Extract token and user from the data property
        const token = response.data.data?.token;
        const user = response.data.data?.user;
        
        // Store token in localStorage if available
        if (token) {
          localStorage.setItem('token', token);
          
          // Store user data if available
          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
          }
          
          // Return the extracted data
          return { token, user };
        } else {
          throw new Error('Authentication successful but no token received');
        }
      } else {
        throw new Error(response.data.message || 'Authentication failed');
      }
    } catch (error) {
      throw error;
    }
  },
  
  // Register new user
  async register(userData: RegisterData): Promise<any> {
    try {
      // Simply map the field names correctly for the API
      const apiData = {
        username: userData.username,
        email: userData.email,
        first_name: userData.firstName,
        last_name: userData.lastName,
        password: userData.password
      };
      
      const response = await authClient.post<AuthResponse>('/auth/register', apiData);
      
      if (response.data.success) {
        // Extract token and user from the data property
        const token = response.data.data?.token;
        const user = response.data.data?.user;
        
        // Store token in localStorage if available
        if (token) {
          localStorage.setItem('token', token);
          
          // Store user data if available
          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
          }
          
          // Return the extracted data
          return { token, user };
        }
      }
      
      throw new Error(response.data.message || 'Registration failed');
    } catch (error) {
      throw error;
    }
  },
  
  // Logout user
  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  
  // Get current authenticated user
  getCurrentUser(): any {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  },
  
  // Check if user is logged in
  isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  },
  
  // Get auth token
  getToken(): string | null {
    return localStorage.getItem('token');
  }
};